import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {installVersionOnHomeTenant} from 'benchling-apps/AppDefinitionActions';
import notifications from 'notifications';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';

import {AppDefinitionVersion} from './BenchlingAppsPage';

export interface InstallOnHomeTenantModalProps {
  version: AppDefinitionVersion;
  appDefinitionId: string;
  currentlyInstalledVersion: string | null;
  onRequestClose: () => void;
  refetchAppDefinition: () => void;
}

export default function InstallOnHomeTenantModal(props: InstallOnHomeTenantModalProps): JSX.Element {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const versionNameAndNumber = `${props.version.name} v${props.version.versionId}`;
  const anotherVersionAlreadyInstalled =
    props.currentlyInstalledVersion != null && props.currentlyInstalledVersion !== props.version.versionId;

  async function onSubmit(): Promise<void> {
    setIsSubmitting(true);
    await installVersionOnHomeTenant(props.appDefinitionId, props.version.versionId)
      .then(() => {
        notifications.success(`Successfully installed ${versionNameAndNumber}.`);
        props.refetchAppDefinition();
        props.onRequestClose();
      })
      .catch((err) => {
        notifications.notifyError(err);
        setIsSubmitting(false);
      });
  }

  return (
    <Modal
      onRequestClose={props.onRequestClose}
      onRequestSubmit={onSubmit}
      title={`Install ${versionNameAndNumber}`}
      buttons={[
        {
          text: 'Cancel',
          submit: false,
          style: ButtonStyle.TERTIARY_GRAY,
          onClick: props.onRequestClose,
        },
        {
          text: 'Install',
          submit: true,
          style: ButtonStyle.PRIMARY,
          disabled: isSubmitting,
        },
      ]}
    >
      {anotherVersionAlreadyInstalled ? (
        <div className={css(styles.marginBottom)}>
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text={`Installing this app version will replace an existing installation (${props.currentlyInstalledVersion})`}
            supportingInfo="Another installation already exists. Only one version of an app can be installed on your tenant."
          />
        </div>
      ) : (
        <></>
      )}
      <p>
        Installing this app version will make it available to configure and use in the main workspace of your
        tenant.
      </p>
      <p>Installing this version will not change the publication state nor share it with other tenants.</p>
      <p>You can still edit draft versions after install. Changes will apply to the installed app.</p>
    </Modal>
  );
}

const styles = StyleSheet.create({
  marginBottom: {
    marginBottom: '20px',
  },
});
